import { useCallback, useState, useEffect } from 'react';
import css from './index.module.scss';
import { Button, IconNode, Typography } from '@components/base';
import { AddUserModal } from '@components/common';
import Images from '@assets/images';
import POCAdd from './poc-add';
import POCDetailCard from '../poc-detail-card';
import { IPointOfContact, Poc } from '@helpers/types/point-of-contact';
import { updateCustomerInfo } from '@services/customer.service';
import notify from '@helpers/toastify-helper';
import { fetchAllUsers } from '@services/user.service';
import POCDetailInvitationCard from '../poc-detail-invitation-card';

interface POCDetailProps {
  customerId: string;
  pocs: Poc[];
  onChange?: (data: any) => void;
  fetchCustomerInfo: () => Promise<void>;
}

const POCDetail = (props: POCDetailProps) => {
  const { pocs, fetchCustomerInfo } = props;
  const [pocState, setPocState] = useState(false);
  const [userList, setUserList] = useState([]);
  const [isAddUserOpen, setIsAddUserOpen] = useState(false);

  const handleAddPoc = useCallback(() => setPocState((prevState) => !prevState), []);

  const handleCancelClick = useCallback(() => {
    handleAddPoc();
  }, [handleAddPoc]);

  const handleAddUserOpen = useCallback(() => setIsAddUserOpen(true), []);
  const handleAddUserClose = useCallback(() => setIsAddUserOpen(false), []);

  useEffect(() => {
    if (pocState) {
      fetchUsersForPocList();
    }
  }, [pocState]);

  const handleFormSubmit = async (data: Omit<IPointOfContact, 'uid'>) => {
    const { onChange, customerId } = props;
    const reqBody = {
      pocs: [data.user_id]
    };
    const response = await updateCustomerInfo(customerId, reqBody, 1);
    if (response.success) {
      const { data } = response;
      notify({ message: 'Point of contact added successfully' });
      handleAddPoc();
      onChange?.(data);
    } else {
      notify({
        message: response.error ?? 'Unable to add Point of contact, try again',
        severity: 'error',
        dismissible: true
      });
    }
  };

  const fetchUsersForPocList = async () => {
    const userList = await fetchAllUsers({
      role: 'external',
      unassigned: true
    });
    if (userList?.success) {
      setUserList(userList.data.users);
    } else {
      notify({ message: userList.error ?? 'Unable to fetch users', severity: 'error' });
    }
  };

  return (
    <div className={css.mainWrapper}>
      <div className={css.headerWrapper}>
        <Typography variant="h4">Point of Contact</Typography>
        <Button
          variant="text"
          onClick={handleAddPoc}
          startIcon={<IconNode src={Images.plusRed} alt="add icon" />}
        >
          Add POC
        </Button>
        <Button
          variant="text"
          onClick={handleAddUserOpen}
          startIcon={<IconNode src={Images.plusRed} alt="add icon" />}
        >
          Add User
        </Button>
      </div>
      <div className={css.pocWrapper}>
        {pocs.map((poc, index) => (
          <POCDetailInvitationCard poc={poc} key={index} fetchCustomerInfo={fetchCustomerInfo} />
        ))}
      </div>
      {pocState && (
        <POCAdd onCancel={handleCancelClick} onSubmit={handleFormSubmit} userList={userList} />
      )}
      {isAddUserOpen && (
        <AddUserModal
          open={isAddUserOpen}
          onClose={handleAddUserClose}
          onComplete={async () => {
            await fetchUsersForPocList();
            await fetchCustomerInfo();
          }}
          onFormSubmit={() => setIsAddUserOpen(false)}
        />
      )}
    </div>
  );
};

export default POCDetail;
